import {BOOKING_STATUS} from "../constants";




export interface JobOffer {
  id:string;
  company: string;
  position: string;
  city: string;
  salary: string;
  postedDate: string;
  type: string;
  tags: string[];
  logoUrl?: string;
}


export interface Job {
  id: string;
  title: string;
  companyName: string;
  logo: string;
  postedDate: string;
  location: string;
  salary: string;
  contract: string;
  experience: string;
  description: string[];
  responsibilities: string[];
  aboutCompany: string;
  languages: string[];
  tags: string[];
}


export interface IReview {
  coachId: string;
  reviewerId: string;
  username: string;
  reviewerName: string;
  comment: string;
  createdAt: Date;
}

export interface IReviewPagination {
  content: IReview[]
}

export interface IJobOffer {
  id: string,
  companyId: string,
  companyName: string,
  workingHours: string,
  companyLogoUrl: string,
  title: string,
  description: string,
  qualifications: string,
  location: string,
  industry: string,
  contractType: string,
  startDate: string,
  endDate: string,
  duration: string,
  salaryRange: string,
  housingSupport: boolean,
  languageLevel: string,
  germanRequired: boolean,
  experience: string,
  education: string,
  tags: string[] ,
  languages : string[] ,
  certifications : string[] ,
  createdAt: string,
  profilesCount: number
}

export interface IStatusHistoryEntry {
  status:
    | 'PENDING'
    | 'SHORTLISTED'
    | 'INVITED_TO_INTERVIEW'
    | 'INTERVIEWED'
    | 'OFFERED'
    | 'ACCEPTED'
    | 'REJECTED';
  changedAt: string;
  changedBy: string;
}

export interface IApplication {
  id: string,
  userId: string,
  jobOfferId: string,
  status: string,
  appliedAt: string,
  applicantName: string,
  location: string,
  applicantProfileImageUrl: string,
  interviewDate?: string;
  interviewTime?: string;
  statusHistory?: IStatusHistoryEntry[];
}

export interface INote {
  id: string,
  applicationId: string,
  title: string,
  type: string,
  content: string,
  createdAt: string,
  date?: string
}

export interface IInterview {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  guests: {
    guestName: string;
    guestEmail: string;
    guestId: string;
  }[];
  status: 'INVITED' | 'UPCOMING' | 'COMPLETED' | 'RESCHEDULED' | 'CANCELLED' | 'DECLINED';
  meetingId: string;
  rescheduleCount?: number;
  declineReason?: string;
  declineNotes?: string;
  rescheduleRequest?: {
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    guests: { guestName: string; guestEmail: string; guestId: string }[];
  };
}


export interface IApplicationWithOffer extends IApplication {
  jobOffer: IJobOffer;
}

export interface IApplicationSocketResponse {
  applicationId: string,
  oldStatus: string,
  newStatus: string,
}

export enum ApplicationStatus {
  PENDING = 'PENDING',
  SHORTLISTED = 'SHORTLISTED',
  INVITED_TO_INTERVIEW = 'INVITED_TO_INTERVIEW',
  INTERVIEWED = 'INTERVIEWED',
  OFFERED = 'OFFERED',
  REJECTED = 'REJECTED',
  ACCEPTED = 'ACCEPTED',
}


export interface IMaterial {
  id: string | undefined,
  userId: string | undefined,
  url: string,
  type: MaterialType | undefined,
}

export interface ISession {
  id?: string;
  coachId: string;
  name: string;
  description: string;
  duration: number;
  isPublic: boolean;
  isPaid: boolean;
  price: number;
  topics: string[];
  buffer: number;
  autoAcceptNewConnections: boolean;
  autoAcceptOldConnections: boolean;
  sessionLink: string;
  active: boolean;
  bookingCount: number;
  weeklyAvailability: { [key: string]: { startTime: string; endTime: string; }[] };
  availabilityExceptions: any[];
}

export type DocRow = {
  kind: 'ORIGINAL' | 'TRANSLATED' | 'SIGNED';
  name: string;
  url: string;
  contentType: string;
  when: string | null;
  label: 'Uploaded' | 'Translated' | 'Verified';
};

export interface INotification {
  id: string,
  message: string,
  timestamp: string,
  type: string,
  isRead: boolean
  userId: boolean
}

export interface EvaluatedItem {
  item: ChecklistItem;
  done: boolean;
  matchedMaterials: { id: string; url: string; type: string; title: string }[];
}

export interface EvaluatedProcess {
  process: ChecklistProcess;
  items: EvaluatedItem[];
  progressPercent: number;
  total: number;
  completed: number;
}


export interface IUser {
  id: string,
  username: string,
  firstname: string,
  lastname: string,
  balance?: number,
  institutionCode?: string,
  education: IEducation[],
  lookingFor?: string,
  domain?: string,
  workExperience: IWorkExperience[],
  lastLoginDate: string,
  email: string,
  password: string,
  role: UserRole,
  createdAt: string; // ISO
  languageLevel?: string; // ISO
  motivation: string,
  expertise: string,
  positiveReviewsPercentage: number,
  sessionsNumber: number,
  socialProfile: string,
  profileImageUrl: string,
  companyLogoUrl: string,
  courseName: string,
  price: 0,
  level: string,
  material: string,
  status: string,
  location: string,
  country?: string,
  materials:
    {
      id: string,
      userId: string,
      title: string,
      description: string,
      url: string,
      type: MaterialType
    }[],
  experience: string,
  languages: string[]
}

/** Admin-only read model returned by GET /admin/users (includes email + applicationsCount). */
export interface IAdminUser {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  role: UserRole;
  profileImageUrl?: string;
  country?: string;
  registrationDate: string; // ISO
  lookingFor?: string;
  domain?: string;
  languageLevel?: string;
  applicationsCount: number;
}

export type DocFile = {
  kind: 'ORIGINAL' | 'CONVERTED' | 'TRANSLATED' | string;
  name: string;
  contentType: string;
  sasUrl: string;
  path: string;
  updatedAt: string; // ISO
};

export type DocBundle = {
  sourceLang: string;
  targetLang: string;
  files: DocFile[];
};

export type CandidateDocsItem = {
  materialId: string;
  title: string | null;
  type: string; // "CV" | "OTHER" | ...
  originalFileName: string;
  originalSasUrl: string;
  hasTranslations: boolean;
  bundles: DocBundle[];
};

export interface CandidateDocumentDto {
  id: string;
  originalFilename?: string | null;
  sourceLang?: string;
  targetLang?: string | null;
  status?: 'PENDING' | 'TRANSLATED' | 'FAILED' | string;
  originalUrl?: string | null;
  translatedUrl?: string | null;
}

export interface IEducation {
  organization: string;
  education: string;
  educationLevel: string;
  location: string;
  startDate: string;
  completionDate: string;
  isCurrent: boolean;
}

export interface IWorkExperience {
  organization: string;
  jobTitle: string;
  location: string;
  startDate: string;
  completionDate: string;
  jobDescription: string;
  isCurrent: boolean;
}


export interface IAuth {
  accessToken: string,
  id: string,
}

export interface IBooking {
  userId: string,
  bookingId?: string,
  chatId?: string,
  coachId: string,
  startDate: string | undefined,
  startTime: string | undefined,
  duration: number | undefined,
  status: BOOKING_STATUS | undefined,
}

export interface IMetadata {
  chatId: string,
  otherParticipantId: string,
  otherParticipantName: string,
  otherParticipantProfileImageUrl: string,
  lastMessage: string,
  lastMessageTimestamp: string,
  unreadMessagesCount: number,
}

export interface IMessage {
  id?: string,
  clientTempId?: string,  // Client-side temporary ID
  chatId: string,
  receiverId: string,
  senderId: string,
  timestamp: Date,
  senderName?: string,
  senderProfileImageUrl?: string;
  content: string,
}

export enum UserRole {
  USER = "USER",
  AGENCY = "AGENCY",
  PARTNER = "PARTNER",
  ADMIN = "ADMIN"
}

export enum UserLevel {
  CERTIFIED = "CERTIFIED",
  EXPERIENCED = "EXPERIENCED",
  MASTER = "MASTER"
}

export enum MaterialType {
  CV = "CV",
  GERMAN_CERTIFICATE = "GERMAN_CERTIFICATE",
  HIGH_SCHOOL_DIPLOMA = "HIGH_SCHOOL_DIPLOMA",
  UNIVERSITY_DIPLOMA = "UNIVERSITY_DIPLOMA",
  PASSPORT = "PASSPORT",
  DEFICIT_BESCHEID = "DEFICIT_BESCHEID",
  ANERKENNUNG = "ANERKENNUNG",
  JOB_CONTRACT = "JOB_CONTRACT",
  OTHER = "OTHER",
  GOOD_STANDING_CERTIFICATE = 'GOOD_STANDING_CERTIFICATE',
  HEPATITIS_VACCINATION = 'HEPATITIS_VACCINATION',
  MEASLES_VACCINATION = 'MEASLES_VACCINATION',
  MEASLES_TITER_LAB = 'MEASLES_TITER_LAB',
}

export type ProcessCode = 'VISA' | 'EMPLOYMENT' | 'PHYSIO_RECOGNITION_DOCS' | 'RELOCATION';

export interface ChecklistItem {
  key: string;
  label: string;
  requiredTypes: MaterialType;
  allowMultiple?: boolean;
  optional?: boolean;
}

export interface ChecklistProcess {
  code: ProcessCode;
  titleKey: string;
  items: ChecklistItem[];
}


export enum ButtonType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  GHOST = "ghost",
  MENU = "menu",
  SUCCESS = "success",
  ERROR = "error"
}

export enum ButtonSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
  LARGER = "larger"
}

export enum ButtonState {
  REGULAR = "regular",
  HOVER = "hover",
  FOCUSED = "focused",
  PRESSED = "pressed",
  DISABLED = "disabled"
}

export enum InputSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
  LARGER = "larger"
}

export enum InputState {
  REGULAR = "Regular",
  HOVER = "Hover",
  FILLED = "Filled",
  ACTIVE_FOCUSED = "ActiveFocused",
  DISABLED = "Disabled",
  ERROR = "Error",
  SUCCESS = "Success"
}

export enum HintType {
  WARNING = "Warning",
  ERROR = "Error",
  SUCCESS = "Success",
  INFO = "Info",
  HELPER = "helper",
  PROGRESS = "Progress"
}


export interface IAvailibility {
  date: string,
  availableSlots: [
    {
      startTime: string;
      endTime: string;

    }
  ]

}

export interface ICompany {
  id: string;
  userId: string;
  name: string,
  industry: string,
  location: string,
  logoUrl: string,
  website: string,
  description: string,
  partnerEmail: string
}

export interface IBookingDetails {
  bookingId?: string,
  userId: string,
  coachId?: string,
  sessionId?: string,
  username?: string,
  profileImageUrl?: string,
  startDate?: string,
  startTime?: string,
  duration?: number,
  status?: BOOKING_STATUS,
  topic: string,
  question: string,
  meetingId?: string,
  sessionName?: string,
  rescheduledByMentor?: boolean,
  rescheduledByMentee?: boolean
}

export interface IBookModalData {
  sessionName: string;
  expertise: string;
  firstname: string;
  lastname: string;
  mentorId: string;
  profileImageUrl: string;
  sessionDescription: string;
  sessionDuration: string;
  sessionTopics: string | string[];
  sessionPrice: number;
  availability: IWeeklyAvailability;
  personalization: IPersonalization;
  oldConnection: boolean;
  newConnection: boolean;
  freeSession: boolean;
  paidSession: boolean;
  id: string;
}

export interface IWeeklyAvailability {
  [key: string]: TimeSlot[];
}

export interface IPersonalization {
  isPublic: boolean;
  isPaid: boolean;
  price: string;
  paidSession: boolean;
  buffer: number;
  newConnection: boolean;
  oldConnection: boolean;
  active: boolean;
}

export interface EditAvailabilityModalData {
  coachId: string;
}

export interface IAvailibilityException {
  id: string,
  startDate: string,
  endDate: string,
  description: string
}

export interface ILanguage {
  name: string;
  code: string;
}

export interface ISkill {
  name: string;
}

export interface IEditModalData {
  dataToEdit: string;
  motivation?: string;
  languages?: string[];
  certificates?: string[];
  skills?: string[];
}

export interface IReview {
  userId: string;
  reviewerId: string;
  rating: number;
  comment: string;
  reviewerName: string;
}

export interface IMaterial {
  id: string,
  userId: string,
  title: string,
  description: string,
  url: string,
  type: MaterialType
}

export interface TimeSlot {
  from: string;
  to: string;
}

export interface DayAvailability {
  name: string;
  enabled: boolean;
  times: { from: string, to: string }[];
}

export interface AvailabilityMap {
  [key: string]: TimeSlot[];
}

export interface ISessionMetadata {
  sessionName: string;
  sessionDescription: string;
  sessionDuration: string;
  sessionTopics: string;
}

export enum JourneyStepType {
  PROFILE = 'PROFILE',
  CONSULTATION = 'CONSULTATION',
  EMPLOYER_MATCH = 'EMPLOYER_MATCH',
  VISA = 'VISA',
}

export enum JourneyStepStatus {
  LOCKED = 'LOCKED',
  AVAILABLE = 'AVAILABLE',
  PENDING_VALIDATION = 'PENDING_VALIDATION',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export interface IJourneyStep {
  id: string;
  type: JourneyStepType;
  order: number;
  priceCents: number;
  currency: string;
  status: JourneyStepStatus;
  recommended?: boolean;
  bookingDate?: string;
}

export interface IJourney {
  id: string;
  userId: string;
  steps: IJourneyStep[];
  currentStepOrder: number;
}

export interface IConsultationSlot {
  id: string;
  startAt: string;
  endAt: string;
  available: boolean;
}

export enum PaymentProvider {
  STRIPE = 'STRIPE',
  BANK_TRANSFER = 'BANK_TRANSFER',
}

export enum ConsultationBookingStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PENDING_VALIDATION = 'PENDING_VALIDATION',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
}

export interface IBankTransferDetails {
  bankName: string;
  accountHolder: string;
  iban: string;
  bic: string;
  referenceCode: string;
  amountCents: number;
  currency: string;
}

export interface ICreateBookingRequest {
  slotId: string;
  provider: PaymentProvider;
  country: string,
}

export interface IConsultationBooking {
  id: string;
  slotId: string;
  status: ConsultationBookingStatus;
  clientSecret?: string;
  bankTransferDetails?: IBankTransferDetails;
}

// ----------------------------------------------------------------------------
// Admin consultation management (backend: /api/v1/admin/consultation/**)
// ----------------------------------------------------------------------------

export enum PaymentStatus {
  PENDING = 'PENDING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
}

export interface IAdminBooking {
  id: string;
  userId: string;
  userFullName: string;
  userEmail: string;
  slotId: string;
  slotStartAt: string;
  slotEndAt: string;
  status: ConsultationBookingStatus;
  stepType: JourneyStepType;
  stepStatus: JourneyStepStatus;
  paymentStatus?: PaymentStatus;
  paymentId?: string; // requires backend AdminBookingDto to expose the payment id
  meetingLink?: string;
  notes?: string;
  createdAt: string;
}

export interface IConfirmMeetingRequest {
  meetingLink: string;
  notes?: string;
}

export interface IRescheduleBookingRequest {
  newSlotId: string;
  notes?: string;
}

export interface ICancelBookingRequest {
  reason: string;
}

export interface IAdminSlot {
  id: string;
  startAt: string;
  endAt: string;
  capacity: number;
  active: boolean;
  bookedCount: number;
  available: boolean;
}

export interface IBulkCreateSlotsRequest {
  fromDate: string;      // ISO date (yyyy-MM-dd)
  toDate: string;        // ISO date (yyyy-MM-dd)
  daysOfWeek: string[];  // e.g. ['MONDAY','TUESDAY']
  dailyStart: string;    // 'HH:mm'
  dailyEnd: string;      // 'HH:mm'
  slotDurationMinutes: number;
  breakMinutes?: number;
  capacity?: number;
}

export interface IBulkCreateSlotsResponse {
  created: number;
  skipped: number;
  slots: IAdminSlot[];
}

export interface IUpdateSlotRequest {
  startAt?: string;
  endAt?: string;
  capacity?: number;
  active?: boolean;
}

// ----------------------------------------------------------------------------
// Admin broadcast email (backend: POST /api/v1/admin/emails/broadcast)
// ----------------------------------------------------------------------------

export enum BroadcastTarget {
  ALL_CANDIDATES = 'ALL_CANDIDATES',
  SELECTED = 'SELECTED',
  WITH_APPLICATION = 'WITH_APPLICATION',
}

export interface IBroadcastEmailRequest {
  subject: string;
  content: string;
  target: BroadcastTarget;
  userIds?: string[];
}

export interface IBroadcastEmailResponse {
  recipients: number;
}
